import {
  Banner,
  Card,
  Grid,
  Input,
  RadioGroupExpander,
  Select,
  Typography,
} from '@oresundsbron/bridge-ui';
import { useForm, useFormParams, useSubscribe } from '@oresundsbron/use-form';
import { BooleanValue } from '@oresundsbron/validators';
import { pipe } from 'fp-ts/lib/function';
import { fromPredicate, alt, map, getOrElse, match } from 'fp-ts/lib/Option';
import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { FormButton } from '../Buttons';
import { FieldSet } from '../Fieldset';
import { OresundPayInfo } from './OresundPayInfo';

export type Data = {
  oresundsPay: BooleanValue;
  paymentProvider: string;
  bizzNumber: string;
  country: string;
  licensePlate: string;
};

type Labels = Omit<Data, 'oresundsPay'> & {
  loading: string;
  paymentContract: string;
  pay: string;
  other: string;
};

export const PaymentContract: FC<
  {
    ns?: string[];
    labels: Labels;
    title: string;
    providerChoices: { label: string; value: string }[];
    countryOptions: { key: string; value: string }[];
    payText: ReactNode;
    description: string;
    placeholders: {
      country: string;
      paymentProvider: string;
    };
    disclaimers: {
      oresundpay: ReactNode;
      brobizz: ReactNode;
    };
  } & useFormParams<Data>
> = ({
  namespace,
  title,
  defaultValues,
  onValidate,
  labels,
  providerChoices = [],
  payText,
  description,
  countryOptions,
  placeholders,
  disclaimers,
  ns,
}) => {
  const oresundPayRef = useRef<string>();
  const {
    registerField,
    getError,
    isActive,
    isValid,
    validate,
    stepNr,
    resetFieldErrors,
    transformData,
  } = useForm<Data>({ namespace, title, defaultValues, onValidate });

  const [paymentProvider, oresundPay] = useSubscribe<Data>(
    namespace,
    'paymentProvider',
    'oresundsPay'
  );

  const inputContent = useMemo(
    () => (
      <>
        <Input
          {...registerField('bizzNumber')}
          label={labels.bizzNumber}
          className="col-span-full"
          error={getError('bizzNumber')}
          disabled={!isActive}
          required
        />
        <Select
          className="col-span-full"
          {...registerField('country')}
          label={labels.country}
          options={countryOptions.map((c) => ({ ...c, label: c.key }))}
          placeholder={placeholders.country}
          error={getError('country')}
          disabled={!isActive}
          required
        />
        <Input
          {...registerField('licensePlate')}
          label={labels.licensePlate}
          className="col-span-full"
          error={getError('licensePlate')}
          disabled={!isActive}
          required
        />
      </>
    ),
    [countryOptions, labels, registerField, getError, placeholders, isActive]
  );

  const paymentProviderContent = useMemo(
    () =>
      pipe(
        paymentProvider,
        fromPredicate((x) => x === 'oresundpay'),
        map(() => <OresundPayInfo>{disclaimers.oresundpay}</OresundPayInfo>),
        alt(() =>
          pipe(
            paymentProvider,
            fromPredicate((x) => x === 'brobizz'),
            map(() => (
              <Typography className="col-span-full">
                {disclaimers.brobizz}
              </Typography>
            ))
          )
        ),
        getOrElse(() => inputContent)
      ),
    [paymentProvider, inputContent, disclaimers]
  );

  useEffect(
    () =>
      pipe(
        oresundPay,
        fromPredicate((x) => x !== oresundPayRef.current),
        match(
          () => {},
          () => {
            oresundPayRef.current = oresundPay as string;
            resetFieldErrors();
            transformData((curr) => ({
              ...curr,
              paymentProvider: '',
              bizzNumber: '',
              licensePlate: '',
            }));
          }
        )
      ),
    [oresundPay, resetFieldErrors, transformData]
  );

  const noAlternativeSelected = oresundPay === '' && paymentProvider === '';
  const noPaymentProviderSelected =
    oresundPay === 'false' && paymentProvider === '';
  const notAllowedPaymentProviderSelected =
    paymentProvider === 'oresundpay' || paymentProvider === 'brobizz';

  const disabled =
    !isActive ||
    !isValid ||
    noAlternativeSelected ||
    noPaymentProviderSelected ||
    notAllowedPaymentProviderSelected;

  return (
    <FieldSet nr={stepNr} title={title} id={namespace}>
      <Typography className="col-span-full">{description}</Typography>
      <RadioGroupExpander.Root
        {...registerField('oresundsPay')}
        className="col-span-full"
        label={labels.paymentContract}
        required
      >
        <RadioGroupExpander.Expander
          label={labels.pay}
          value="true"
          disabled={!isActive}
        >
          <Banner className="mt-4">{payText}</Banner>
        </RadioGroupExpander.Expander>
        <RadioGroupExpander.Expander
          label={labels.other}
          value="false"
          disabled={!isActive}
        >
          <Card.Root className="mt-4">
            <Card.Content>
              <Grid className="gap-4">
                <Select
                  {...registerField('paymentProvider')}
                  placeholder={placeholders.paymentProvider}
                  label={labels.paymentProvider}
                  options={providerChoices}
                  className="col-span-full"
                  error={getError('paymentProvider')}
                  disabled={!isActive}
                  required
                />
                {paymentProviderContent}
              </Grid>
            </Card.Content>
          </Card.Root>
        </RadioGroupExpander.Expander>
      </RadioGroupExpander.Root>
      {getError('oresundsPay') !== undefined ? (
        <Banner className="col-span-full" color="negative">
          {getError('oresundsPay')}
        </Banner>
      ) : null}

      <FormButton
        loadingLabel={labels.loading}
        namespace={namespace}
        onClick={validate}
        disabled={disabled}
      />
    </FieldSet>
  );
};
